/**
 * Subscription plans shown on the /pricing presentation page.
 *
 * Single source of truth for plan prices and allowances on the frontend.
 * Presentation only: nothing here is enforced, billed, or sent to the backend.
 * Customer entitlements and the existing pay-per-share flow are unaffected.
 *
 * Prices are listed per currency: ILS (shown in Hebrew) and USD (shown in English).
 * The USD prices are set separately, not converted from ILS.
 * The monthly fee is separate from the usage charge per shared trip day.
 */

const CURRENCY_ILS = 'ILS';
const CURRENCY_USD = 'USD';

// Per-trip rules, same for all plans:
// trip price = max(minTripCharge, days * sharedTripDay)
//            + max(0, users - INCLUDED_USERS_PER_TRIP) * additionalUser
// additionalUser is a one-time charge per user beyond the included ones, per trip
// (not monthly, not daily).
const PRICING_CURRENCIES = Object.freeze({
  [CURRENCY_ILS]: Object.freeze({ symbol: '₪', sharedTripDay: 5, minTripCharge: 15, additionalUser: 15 }),
  [CURRENCY_USD]: Object.freeze({ symbol: '$', sharedTripDay: 2, minTripCharge: 6, additionalUser: 6 }),
});

const INCLUDED_USERS_PER_TRIP = 4;

// WhatsApp allowance bases: once for the whole trip, or multiplied by trip days.
const WHATSAPP_BASIS_PER_TRIP = 'perTrip';
const WHATSAPP_BASIS_PER_TRIP_DAY = 'perTripDay';

// tripDesigns: the ride manager picks the trip page's design (classic,
// poster, ...) and edits it, e.g. the poster's stops, names and photos.
const PRICING_PLANS = Object.freeze([
  Object.freeze({
    id: 'free',
    name: 'Free',
    monthlyPrice: Object.freeze({ [CURRENCY_ILS]: 0, [CURRENCY_USD]: 0 }),
    aiRequestsPerTripDay: 2,
    whatsappPerTraveler: 1,
    whatsappBasis: WHATSAPP_BASIS_PER_TRIP,
    whiteLabel: false,
    tripDesigns: false,
    recommended: false,
  }),
  Object.freeze({
    id: 'smart',
    name: 'Smart',
    monthlyPrice: Object.freeze({ [CURRENCY_ILS]: 49, [CURRENCY_USD]: 15 }),
    aiRequestsPerTripDay: 5,
    whatsappPerTraveler: 1,
    whatsappBasis: WHATSAPP_BASIS_PER_TRIP,
    whiteLabel: false,
    tripDesigns: false,
    recommended: false,
  }),
  Object.freeze({
    id: 'pro',
    name: 'Pro',
    monthlyPrice: Object.freeze({ [CURRENCY_ILS]: 59, [CURRENCY_USD]: 19 }),
    aiRequestsPerTripDay: 10,
    whatsappPerTraveler: 10,
    whatsappBasis: WHATSAPP_BASIS_PER_TRIP_DAY,
    whiteLabel: false,
    tripDesigns: true,
    recommended: true,
  }),
  Object.freeze({
    id: 'business',
    name: 'Business',
    monthlyPrice: Object.freeze({ [CURRENCY_ILS]: 159, [CURRENCY_USD]: 45 }),
    aiRequestsPerTripDay: 30,
    whatsappPerTraveler: 20,
    whatsappBasis: WHATSAPP_BASIS_PER_TRIP_DAY,
    whiteLabel: true,
    tripDesigns: true,
    recommended: false,
  }),
]);

// Scenario used by the "usage example" section of the pricing page.
const PRICING_USAGE_EXAMPLE = Object.freeze({ tripDays: 7, travelers: 4 });

// Trip-price examples (same on every plan) showing the minimum and additional users.
const TRIP_PRICE_EXAMPLES = Object.freeze([
  Object.freeze({ tripDays: 1, users: 4 }),
  Object.freeze({ tripDays: 3, users: 4 }),
  Object.freeze({ tripDays: 7, users: 4 }),
  Object.freeze({ tripDays: 7, users: 5 }),
  Object.freeze({ tripDays: 7, users: 6 }),
  Object.freeze({ tripDays: 7, users: 8 }),
]);

// Pricing CTAs open a WhatsApp chat with sales; there is no self-serve plan purchase.
const PRICING_CONTACT_WHATSAPP_NUMBER = '972526867915';

/**
 * Usage charge for one shared trip. Identical on every plan and never includes
 * the monthly subscription.
 */
function calculateTripPrice(tripDays, users, currency = CURRENCY_ILS) {
  const rates = PRICING_CURRENCIES[currency];
  const daysCharge = Math.max(rates.minTripCharge, tripDays * rates.sharedTripDay);
  const additionalUsers = Math.max(0, users - INCLUDED_USERS_PER_TRIP);
  const additionalUsersCharge = additionalUsers * rates.additionalUser;

  return {
    daysCharge,
    additionalUsers,
    additionalUsersCharge,
    total: daysCharge + additionalUsersCharge,
  };
}

/**
 * Allowances and the usage charge for one shared trip on a given plan.
 * Every registered traveler counts as a trip user.
 * The monthly subscription is deliberately NOT included in tripCharge.
 */
function calculateTripUsage(plan, tripDays, travelers, currency = CURRENCY_ILS) {
  const whatsappPerTraveler = plan.whatsappBasis === WHATSAPP_BASIS_PER_TRIP_DAY
    ? plan.whatsappPerTraveler * tripDays
    : plan.whatsappPerTraveler;

  return {
    tripCharge: calculateTripPrice(tripDays, travelers, currency).total,
    aiRequests: plan.aiRequestsPerTripDay * tripDays,
    whatsappPerTraveler,
    whatsappTotal: whatsappPerTraveler * travelers,
  };
}

module.exports = {
  CURRENCY_ILS,
  CURRENCY_USD,
  PRICING_CURRENCIES,
  INCLUDED_USERS_PER_TRIP,
  WHATSAPP_BASIS_PER_TRIP,
  WHATSAPP_BASIS_PER_TRIP_DAY,
  PRICING_PLANS,
  PRICING_USAGE_EXAMPLE,
  TRIP_PRICE_EXAMPLES,
  PRICING_CONTACT_WHATSAPP_NUMBER,
  calculateTripPrice,
  calculateTripUsage,
};
